import React, { useState } from 'react';
import { projectFirestore, timestamp } from '../../firebase/config';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../../cloudinary/config';
import useSections from '../../hooks/useSections';

const MigrationManager = () => {
    const { sections: allSections } = useSections();
    const ALL_COLLECTIONS = allSections.map(s => ({ value: s.key, label: s.label }));
    const [scanning, setScanning] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [firebaseImages, setFirebaseImages] = useState([]);
    const [scanDone, setScanDone] = useState(false);
    const [log, setLog] = useState([]);
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    const addLog = (msg, type = 'info') => {
        setLog(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
    };

    const handleScan = async () => {
        setScanning(true);
        setFirebaseImages([]);
        setLog([]);
        setScanDone(false);
        addLog('بدأ الفحص...');

        const found = [];
        for (const coll of ALL_COLLECTIONS) {
            try {
                const snap = await projectFirestore.collection(coll.value).get();
                let count = 0;
                snap.forEach(doc => {
                    const data = doc.data();
                    if (data.url && data.url.includes('firebasestorage.googleapis.com')) {
                        found.push({ id: doc.id, collection: coll.value, collectionLabel: coll.label, url: data.url, data });
                        count++;
                    }
                });
                if (count > 0) addLog(coll.label + ' (' + coll.value + '): وُجد ' + count + ' صورة من Firebase', 'warning');
            } catch (err) {
                addLog('خطأ في فحص ' + coll.label + ': ' + err.message, 'error');
            }
        }

        setFirebaseImages(found);
        setScanDone(true);
        setScanning(false);
        addLog('انتهى الفحص — إجمالي ' + found.length + ' صورة من Firebase Storage', found.length > 0 ? 'warning' : 'success');
    };

    const uploadUrlToCloudinary = (imageUrl, folder) => {
        return new Promise((resolve, reject) => {
            const url = 'https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD_NAME + '/image/upload';
            const formData = new FormData();
            formData.append('file', imageUrl);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            formData.append('folder', folder);

            const xhr = new XMLHttpRequest();
            xhr.open('POST', url);
            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.secure_url);
                } else {
                    reject(new Error('Upload failed: ' + xhr.status));
                }
            };
            xhr.onerror = () => reject(new Error('Connection error'));
            xhr.send(formData);
        });
    };

    const handleMigrate = async () => {
        if (!window.confirm('سيتم نقل ' + firebaseImages.length + ' صورة من Firebase إلى Cloudinary. هذه العملية قد تستغرق وقتاً. متأكد؟')) return;

        setMigrating(true);
        setProgress({ done: 0, total: firebaseImages.length });
        addLog('بدأ النقل إلى Cloudinary...');

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < firebaseImages.length; i++) {
            const img = firebaseImages[i];
            try {
                const newUrl = await uploadUrlToCloudinary(img.url, img.collection);

                await projectFirestore.collection(img.collection).doc(img.id).update({
                    url: newUrl,
                    oldFirebaseUrl: img.url,
                    migratedAt: timestamp(),
                    sources: { ...(img.data.sources || {}), cloudinary_migrated: newUrl },
                });

                successCount++;
                addLog('✅ ' + img.collectionLabel + '/' + img.id + ' — تم النقل بنجاح', 'success');
            } catch (err) {
                failCount++;
                addLog('❌ ' + img.collectionLabel + '/' + img.id + ' — فشل: ' + err.message, 'error');
            }
            setProgress({ done: i + 1, total: firebaseImages.length });
        }

        addLog('انتهى النقل — نجح: ' + successCount + ', فشل: ' + failCount, successCount > 0 ? 'success' : 'error');
        setMigrating(false);

        if (successCount > 0) {
            addLog('جاري إعادة الفحص...');
            await handleScan();
        }
    };

    const handleDownloadAll = async () => {
        if (firebaseImages.length === 0) return;
        addLog('بدأ التنزيل...');

        for (let i = 0; i < firebaseImages.length; i++) {
            const img = firebaseImages[i];
            try {
                const response = await fetch(img.url);
                if (!response.ok) throw new Error('HTTP ' + response.status);
                const blob = await response.blob();
                const ext = blob.type.includes('png') ? '.png' : '.jpg';
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = img.collection + '_' + img.id + ext;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
                addLog('⬇ ' + img.collectionLabel + '/' + img.id + ' — تم التنزيل', 'success');
            } catch (err) {
                addLog('❌ ' + img.collectionLabel + '/' + img.id + ' — فشل التنزيل: ' + err.message, 'error');
            }
            await new Promise(r => setTimeout(r, 500));
        }
        addLog('انتهى التنزيل');
    };

    const handleDeleteBroken = async () => {
        if (!window.confirm('سيتم حذف ' + firebaseImages.length + ' سجل صور Firebase من قاعدة البيانات نهائياً. هل أنت متأكد؟')) return;

        addLog('بدأ الحذف...');
        let deleted = 0;
        for (const img of firebaseImages) {
            try {
                await projectFirestore.collection(img.collection).doc(img.id).delete();
                deleted++;
                addLog('🗑 ' + img.collectionLabel + '/' + img.id + ' — تم الحذف', 'success');
            } catch (err) {
                addLog('❌ فشل حذف ' + img.collectionLabel + '/' + img.id + ': ' + err.message, 'error');
            }
        }
        addLog('تم حذف ' + deleted + ' سجل', 'success');
        await handleScan();
    };

    const perc = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    return (
        <div className="container my-4">
            <h4 className="text-center mb-4">🔄 نقل صور Firebase Storage</h4>

            <div className="alert alert-info">
                <strong>ما هذه الأداة؟</strong><br />
                تبحث عن الصور المخزنة على Firebase Storage في جميع الأقسام ({ALL_COLLECTIONS.length} قسم)، وتتيح لك:
                <ul className="mb-0 mt-1">
                    <li><strong>نقل إلى Cloudinary</strong> — يرفع الصورة تلقائياً ويحدّث الرابط</li>
                    <li><strong>تنزيل</strong> — يحفظ جميع الصور على جهازك</li>
                    <li><strong>حذف السجلات</strong> — يزيل سجلات الصور غير المتاحة من قاعدة البيانات</li>
                </ul>
            </div>

            <div className="text-center mb-4">
                <button className="btn btn-primary btn-lg" onClick={handleScan} disabled={scanning || migrating}>
                    {scanning ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span> جاري الفحص...</>
                    ) : '🔍 فحص جميع الأقسام'}
                </button>
            </div>

            {scanDone && (
                <div className={'alert text-center ' + (firebaseImages.length > 0 ? 'alert-warning' : 'alert-success')}>
                    {firebaseImages.length > 0
                        ? '⚠ تم العثور على ' + firebaseImages.length + ' صورة من Firebase Storage'
                        : '✅ لا توجد صور على Firebase Storage — جميع الصور على Cloudinary'}
                </div>
            )}

            {firebaseImages.length > 0 && (
                <div className="d-flex justify-content-center flex-wrap gap-2 mb-4">
                    <button className="btn btn-success" onClick={handleMigrate} disabled={migrating}>
                        {migrating ? (
                            <><span className="spinner-border spinner-border-sm me-2"></span> جاري النقل ({perc}%)...</>
                        ) : '☁ نقل ' + firebaseImages.length + ' صورة إلى Cloudinary'}
                    </button>
                    <button className="btn btn-outline-primary" onClick={handleDownloadAll} disabled={migrating}>
                        ⬇ تنزيل جميع الصور
                    </button>
                    <button className="btn btn-outline-danger" onClick={handleDeleteBroken} disabled={migrating}>
                        🗑 حذف السجلات فقط
                    </button>
                </div>
            )}

            {migrating && (
                <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                        <small>{progress.done} / {progress.total}</small>
                        <small>{perc}%</small>
                    </div>
                    <div className="progress" style={{ height: 8 }}>
                        <div className="progress-bar progress-bar-striped progress-bar-animated bg-success" style={{ width: perc + '%' }}></div>
                    </div>
                </div>
            )}

            {firebaseImages.length > 0 && (
                <div className="mb-4">
                    <h6 className="text-center mb-2">معاينة الصور ({firebaseImages.length})</h6>
                    <div className="row g-2">
                        {firebaseImages.slice(0, 20).map((img) => (
                            <div className="col-md-2 col-sm-3 col-4" key={img.id + img.collection}>
                                <div className="card shadow-sm">
                                    <img src={img.url} alt="" style={{ width: '100%', height: 80, objectFit: 'cover' }}
                                        onError={(e) => { e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="80" fill="%23fee"><rect width="200" height="80"/><text x="50%" y="50%" text-anchor="middle" fill="%23c33" font-size="11" dy=".3em">غير متاحة</text></svg>'); }}
                                        loading="lazy" />
                                    <div className="text-center" style={{ fontSize: 10, padding: 2 }}>{img.collectionLabel}</div>
                                </div>
                            </div>
                        ))}
                        {firebaseImages.length > 20 && (
                            <div className="col-12 text-center text-muted mt-1">
                                <small>... و {firebaseImages.length - 20} صورة أخرى</small>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {log.length > 0 && (
                <div className="card shadow-sm">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <span>📋 سجل العمليات</span>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setLog([])}>مسح</button>
                    </div>
                    <div className="card-body p-2" style={{ maxHeight: 250, overflowY: 'auto', fontSize: 13 }}>
                        {log.map((entry, i) => (
                            <div key={i} className={'mb-1 ' + (entry.type === 'error' ? 'text-danger' : entry.type === 'success' ? 'text-success' : entry.type === 'warning' ? 'text-warning' : 'text-muted')}>
                                <small className="text-secondary me-1">[{entry.time}]</small>
                                {entry.msg}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MigrationManager;
